import React from 'react';
import { Box, Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { RATING_SCALE_LABEL } from '../../constants';
import type { NumericQuestion } from '../../types';

interface RatingScaleToggleProps {
    numericQuestion: NumericQuestion | null;
    fieldIndex: number;
}

/**
 * Toggle to enable/disable rating scale for a numeric question.
 *
 * @param {RatingScaleToggleProps} props
 * @returns {JSX.Element}
 */
const RatingScaleToggle: React.FC<RatingScaleToggleProps> = ({ numericQuestion, fieldIndex }) => {
    const formContext = useFormContext();

    const handleChange = React.useCallback(
        (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            const questionToUpdate = formContext.getValues(`questions.${fieldIndex}` as const);
            const updatedQuestion = {
                ...questionToUpdate,
                useRatingScale: checked,
            };

            formContext.setValue(`questions.${fieldIndex}` as const, updatedQuestion, {
                shouldValidate: false,
                shouldDirty: true,
            });
        },
        [fieldIndex, formContext]
    );

    const tooltipTitle =
        'Enables a numeric rating scale for this question. Rating values are weighted and applied ' +
        'in formula-based scores as following: ' +
        RATING_SCALE_LABEL;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 'none', minWidth: 0, mt: 0.5 }}>
            <FormControlLabel
                control={
                    <Checkbox checked={Boolean(numericQuestion?.useRatingScale)} onChange={handleChange} size="small" color="primary" />
                }
                label="Use rating scale in formulas"
                sx={{
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': {
                        fontSize: (theme) => theme.typography.body2.fontSize,
                        color: 'text.secondary',
                    },
                }}
            />
            <Tooltip title={tooltipTitle} arrow placement="top">
                <IconButton size="small" sx={{ p: 0.25 }} aria-label="Rating scale info">
                    <InfoOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default RatingScaleToggle;
